import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Github } from "lucide-react";
import { IDS } from "@/constants/testIds";
import { OWNER, GITHUB_BADGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = {
  login: OWNER.githubUsername,
  name: OWNER.name,
  avatar_url: null,
  public_repos: 12,
  followers: 24,
  following: 18,
  total_stars: 7,
  html_url: OWNER.socials.github,
  top_repos: [],
  bio: "New to life.",
};

export default function GitHubSnippet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/github/${OWNER.githubUsername}`, { timeout: 8000 });
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error("github fetch failed", e);
        if (!cancelled) setData(FALLBACK);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const d = data || FALLBACK;

  const stats = [
    { key: "repos", testid: IDS.github.repos, k: d.public_repos, v: "Public repos" },
    { key: "followers", testid: IDS.github.followers, k: d.followers, v: "Followers" },
    { key: "stars", testid: IDS.github.stars, k: d.total_stars, v: "Total stars" },
    { key: "following", testid: IDS.github.following, k: d.following, v: "Following" },
  ];

  return (
    <section
      id="github"
      data-testid={IDS.github.root}
      className="border-b border-border py-24 relative"
    >
      <div className="pointer-events-none absolute top-24 right-10 h-40 w-40 rounded-full opacity-30 blur-3xl bg-[#00FFFF]/40"></div>

      <div className="mx-auto max-w-7xl px-4 sm:px-8 relative">
        <div className="flex items-center gap-3 font-jet text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
          <span>03</span><span className="h-px w-8 bg-border"></span><span>GitHub snippet</span>
        </div>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <h2 className="font-mono-brut font-black uppercase text-4xl sm:text-6xl tracking-tighter leading-none">
            Live from <span className="text-[#E3FF00]">git.</span>
          </h2>
          <a
            href={d.html_url || OWNER.socials.github}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border border-[#F4F4F0] px-4 py-2 font-mono-brut text-xs uppercase tracking-widest hover:bg-[#E3FF00] hover:text-[#0A0A0A] hover:border-[#E3FF00] transition"
          >
            <Github size={14} /> @{OWNER.githubUsername}
          </a>
        </div>

        {/* Stats Bento */}
        <div className="grid grid-cols-2 md:grid-cols-4 border border-border">
          {stats.map((s, i) => (
            <motion.div
              key={s.key}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`p-6 sm:p-8 ${i < 3 ? "md:border-r" : ""} ${i < 2 ? "border-r border-b md:border-b-0" : "border-b md:border-b-0"} border-border relative overflow-hidden`}
            >
              <div className="font-mono-brut text-5xl sm:text-6xl font-black tracking-tighter">
                {loading ? "—" : s.k}
              </div>
              <div className="font-jet text-[10px] uppercase tracking-widest text-muted-foreground mt-3">
                {s.v}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Badges */}
        <div className="mt-6 border border-border bg-[#141414] p-6">
            <div className="font-jet text-[11px] uppercase tracking-widest text-muted-foreground mb-6">
              Achievements
            </div>
            <div className="flex flex-wrap gap-4">
              {GITHUB_BADGES.map((b, i) => (
                <motion.div
                  key={b.name}
                  data-testid={IDS.github.badge(b.name)}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  whileHover={{ rotate: 0, y: -4 }}
                  className="inline-flex flex-col items-start px-3 py-2 border-2 border-[#0A0A0A]"
                  style={{
                    backgroundColor: b.color,
                    color: "#0A0A0A",
                    transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (2 + i)}deg)`,
                    boxShadow: "4px 4px 0 0 #0A0A0A",
                  }}
                >
                  <span className="font-mono-brut font-black text-sm uppercase tracking-tight leading-none">{b.label}</span>
                  <span className="font-jet text-[9px] uppercase tracking-widest mt-1">{b.desc}</span>
                </motion.div>
              ))}
            </div>
        </div>
      </div>
    </section>
  );
}
